import { Alert, Box, Card, Heading, SimpleGrid, Skeleton, VStack } from '@chakra-ui/react';
import { BellAlertIcon } from '@heroicons/react/24/outline';
import { useFocusedBranchContext } from '@packages/core/branch/focused-branch-context';
import { useTranslate } from '@tolgee/react';
import { useEffect, useState } from 'react';
import { DateRange } from '../../common/reservation-analytic-service';
import SkeletonCard from '../../common/skeleton-card';
import getReservationOverViewMetric from '../logic/use-get-overview-metrics';
import MetricCard from './metric-card';

export default function ReservationOverview(props: { dateRange: DateRange }) {
  const branch = useFocusedBranchContext();
  const { t } = useTranslate();

  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: props.dateRange.startDate,
    endDate: props.dateRange.endDate,
  });

  useEffect(() => {
    setDateRange({
      startDate: props.dateRange.startDate,
      endDate: props.dateRange.endDate,
    });
  }, [props.dateRange]);

  const { analytics, isLoading, error } = getReservationOverViewMetric(branch.branch?.id ?? '', dateRange);

  if (isLoading) {
    return (
      <VStack gap={4} align="stretch">
        <Skeleton height="40px" />
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={6}>
          {Array.from({ length: 4 }).map((_, index) => (
            <SkeletonCard key={index} />
          ))}
        </SimpleGrid>
      </VStack>
    );
  }

  if (error) {
    return (
      <Alert.Root status="error" borderRadius="md">
        <BellAlertIcon className="w-4 h-4" />
        <Box>
          <Alert.Title>Error loading analytics!</Alert.Title>
          <Alert.Description>{error.message}</Alert.Description>
        </Box>
      </Alert.Root>
    );
  }

  if (!analytics) {
    return (
      <Alert.Root status="info" borderRadius="md">
        <BellAlertIcon className="w-4 h-4" />
        <Alert.Title>No data available</Alert.Title>
      </Alert.Root>
    );
  }

  const { current, trends } = analytics;

  return (
    <Card.Root>
      <Card.Header>
        <Card.Title>
          <Heading fontWeight={'semibold'} fontSize="xl" color="gray.900">
            {t('reservation.report.overview', 'Overview')}
          </Heading>
        </Card.Title>
      </Card.Header>
      <Card.Body p={0}>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={4}>
          <MetricCard
            title={t('reservation.report.overview.metric.totalReservations.title', 'Số lượng đặt chỗ đã nhận')}
            value={current.totalReservations}
            trend={trends.totalReservations}
            subtitle={t('reservation.report.overview.metric.totalReservations.subtitle', 'reservations')}
          />

          <MetricCard
            title={t('reservation.report.overview.metric.totalGuests.title', 'Tổng số khách đã phục vụ')}
            value={current.totalGuests}
            trend={trends.totalGuests}
            subtitle={t(
              'reservation.report.overview.metric.totalGuests.subtitle',
              'average {guestsPerBooking} guests per booking',
              { guestsPerBooking: '3.8' },
            )}
          />

          <MetricCard
            title={t('reservation.report.overview.metric.showUpRate.title', 'Hiệu suất đặt chỗ')}
            value={`${current.showUpRate.toString()}%`}
            trend={trends.showUpRate}
            subtitle={t('reservation.report.overview.metric.showUpRate.subtitle', 'customers actually showed up')}
          />

          <MetricCard
            title={t('reservation.report.overview.metric.cancelledReservations.title', 'Số lượng đặt chỗ đã hủy')}
            value={current.cancelledReservations}
            trend={trends.cancellationRate}
            subtitle={t('reservation.report.overview.metric.cancelledReservations.subtitle', 'reservations')}
          />
        </SimpleGrid>
      </Card.Body>
    </Card.Root>
  );
}
